import * as React from 'react';
import Modal from 'antd/lib/modal';
import Checkbox, { CheckboxChangeEvent } from 'antd/lib/checkbox';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import Divider from 'antd/lib/divider';
import Input from 'antd/lib/input';
import Form from 'antd/lib/form';
import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { DestinationSummary, ReleaseStatus, RunwayResponseType } from '@/types';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { globalMessage } from '@/utils/globalMessage';
import { WorkflowRunUpdatable } from '@/actions/workflowRun';
import { useCallUpdatable } from '@/utils/hooks/helpers';
import {
    selectDestinationInfoByDestinationStepConfigId,
    selectRelease,
    selectWorkflowId,
    selectWorkflowRunId,
} from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import { DestinationCounter } from '../appHome/DestinationCounter';

export interface EditDestinationsModalProps {
    visible: boolean;
    onClose: () => void;
    appId: string;
    orgId: string;
    releaseId: string;
}

export const EditDestinationsModal: React.FC<EditDestinationsModalProps> = ({
    visible,
    onClose,
    appId,
    orgId,
    releaseId,
}) => {
    const [form] = Form.useForm<{ selectedDestinationStepConfigIds: string[] }>();
    const [searchQuery, setSearchQuery] = React.useState('');

    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
    const workflowRunId = useSelector((state: RootState) => selectWorkflowRunId(state, releaseId));
    const destinationSummaries = useSelector(
        (state: RootState) => selectRelease(state, releaseId).destinationSummaries,
    );

    const destinationSummaryByStepConfigId = React.useMemo(() => {
        const map = new Map<string, DestinationSummary>();
        for (const s of destinationSummaries ?? []) {
            map.set(s.stepConfigId, s);
        }
        return map;
    }, [destinationSummaries]);

    const destinationInfoByStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );

    const destinationRows = React.useMemo(
        () => Object.values(destinationInfoByStepConfigId),
        [destinationInfoByStepConfigId],
    );

    const initialSelectedDestinationStepConfigIds = React.useMemo(() => {
        const selected: string[] = [];
        for (const row of destinationRows) {
            const status =
                destinationSummaryByStepConfigId.get(row.stepConfigId)?.destinationStatus ?? ReleaseStatus.Active;
            if (status !== ReleaseStatus.Skipped) {
                selected.push(row.stepConfigId);
            }
        }
        return selected;
    }, [destinationRows, destinationSummaryByStepConfigId]);

    React.useEffect(() => {
        if (visible) {
            setSearchQuery('');
        }
    }, [visible]);

    const [updateDestinationStatuses, isUpdateLoading, updateResponseType, updateError] = useCallUpdatable(
        WorkflowRunUpdatable.UpdateDestinationStatuses,
        {
            orgId,
            workflowId: workflowId!,
            workflowRunId: workflowRunId!,
        },
        destinationRows.map((r) => r.stepConfigId).join(','),
    );

    React.useEffect(() => {
        if (updateResponseType === RunwayResponseType.Success) {
            globalMessage.success('Destinations updated successfully');
            onClose();
        }
    }, [updateResponseType, onClose]);

    React.useEffect(() => {
        if (updateError) {
            globalMessage.error(updateError);
        }
    }, [updateError]);

    const handleConfirm = () => {
        const destinationUpdates: Array<{ destinationStepConfigId: string; status: ReleaseStatus }> = [];
        const selectedIds = new Set<string>(
            (form.getFieldValue('selectedDestinationStepConfigIds') as string[] | undefined) ?? [],
        );
        const initialSelectionSet = new Set(initialSelectedDestinationStepConfigIds);

        destinationRows.forEach((row) => {
            const wasIncluded = initialSelectionSet.has(row.stepConfigId);
            const isNowIncluded = selectedIds.has(row.stepConfigId);

            if (wasIncluded !== isNowIncluded) {
                destinationUpdates.push({
                    destinationStepConfigId: row.stepConfigId,
                    status: isNowIncluded ? ReleaseStatus.Active : ReleaseStatus.Skipped,
                });
            }
        });

        if (destinationUpdates.length === 0) {
            onClose();
            return;
        }

        updateDestinationStatuses({ destinationUpdates });
    };

    const handleSelectAll = (e: CheckboxChangeEvent) => {
        if (e.target.checked) {
            form.setFieldsValue({
                selectedDestinationStepConfigIds: filteredDestinationRows.map((r) => r.stepConfigId),
            });
        } else {
            form.setFieldsValue({ selectedDestinationStepConfigIds: [] });
        }
    };

    const filteredDestinationRows = React.useMemo(() => {
        if (!searchQuery.trim()) {
            return destinationRows;
        }
        const query = searchQuery.toLowerCase().trim();
        return destinationRows.filter((row) => row.name.toLowerCase().includes(query));
    }, [destinationRows, searchQuery]);

    const selectedDestinationStepConfigIds =
        (Form.useWatch('selectedDestinationStepConfigIds', form) as string[] | undefined) ?? [];
    const selectedDestinationStepConfigIdSet = React.useMemo(
        () => new Set(selectedDestinationStepConfigIds),
        [selectedDestinationStepConfigIds],
    );

    const allSelected =
        filteredDestinationRows.length > 0 &&
        filteredDestinationRows.every((r) => selectedDestinationStepConfigIdSet.has(r.stepConfigId));
    const someSelected = filteredDestinationRows.some((r) => selectedDestinationStepConfigIdSet.has(r.stepConfigId));

    const selectedCount = selectedDestinationStepConfigIds.length;
    const totalCount = destinationRows.length;

    return (
        <Modal
            title="Edit destinations"
            open={visible}
            onCancel={onClose}
            okButtonProps={{
                loading: isUpdateLoading,
                disabled: selectedCount === 0,
                icon: <CheckOutlined />,
            }}
            okText={`Include ${selectedCount} of ${totalCount} destinations for this release`}
            onOk={handleConfirm}
            cancelText="Cancel"
            destroyOnClose
            width={720}
        >
            <Form
                form={form}
                initialValues={{ selectedDestinationStepConfigIds: initialSelectedDestinationStepConfigIds }}
                preserve={false}
            >
                <Text style={{ color: antGrey.grey7, display: 'block', marginBottom: 24 }}>
                    Choose which destinations to include for this release.
                </Text>

                <Input
                    placeholder="Search destinations..."
                    prefix={<SearchOutlined style={{ color: antGrey.grey6 }} />}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ marginBottom: 24 }}
                    allowClear
                />

                <Flex align="center" gap={12} style={{ marginBottom: 16 }}>
                    <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={handleSelectAll}
                    >
                        <Text strong>Select all</Text>
                    </Checkbox>
                </Flex>
                <Divider style={{ margin: '16px 0' }} />

                <Form.Item name="selectedDestinationStepConfigIds">
                    <Checkbox.Group style={{ width: '100%' }}>
                        <div
                            style={{
                                maxHeight: 400,
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 16,
                                flex: 1,
                            }}
                        >
                            {filteredDestinationRows.map((row) => {
                                const isSelected = selectedDestinationStepConfigIdSet.has(row.stepConfigId);
                                return (
                                    <Checkbox
                                        key={row.stepConfigId}
                                        value={row.stepConfigId}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 16,
                                            padding: '12px 20px',
                                            backgroundColor: runwayColors.white,
                                            borderRadius: 8,
                                            border: `1px solid ${isSelected ? runwayColors.info : antGrey.grey5}`,
                                            opacity: isSelected ? 1 : 0.5,
                                            cursor: 'pointer',
                                            width: '100%',
                                        }}
                                    >
                                        <Flex align="center" gap={8} style={{ flex: 1 }}>
                                            <DestinationCounter
                                                destinations={row?.stepConfigId ? [row] : []}
                                                size={24}
                                            />
                                            <Text style={{ fontWeight: 500, fontSize: 14, color: antGrey.grey8 }}>
                                                {row.name}
                                            </Text>
                                        </Flex>
                                    </Checkbox>
                                );
                            })}
                        </div>
                    </Checkbox.Group>
                </Form.Item>
            </Form>
        </Modal>
    );
};
